import { COW_MODE } from "../config/cowMode";

export interface Step {
  n: string;
  title: string;
  body: string;
  accent: string;
}

export interface SiteCopy {
  docTitle: string;
  hero: {
    eyebrow: string;
    wordTop: string;
    wordBottom: string;
    tagline: string;
    ctaAria: string;
    ctaLine1: string;
    ctaLine2: string;
    ctaHint: string;
  };
  marquee: { label: string };
  how: { label: string; heading: string; steps: Step[] };
  counter: { label: string };
  submit: { label: string; heading: string; blurb: string };
  footer: { wordmark: string; tagline: string; note?: string; copyright: string };
}

const classic: SiteCopy = {
  docTitle: "Compliment Hotline — Pick up. Say something nice.",
  hero: {
    eyebrow: "📞 An analog art piece for a digital disco",
    wordTop: "COMPLIMENT",
    wordBottom: "HOTLINE",
    tagline: "Pick up the phone. Hear a compliment from a stranger. Leave one for the next person.",
    ctaAria: "Pick up the phone to hear a compliment",
    ctaLine1: "pick up the phone",
    ctaLine2: "for a pick-me-up",
    ctaHint: "▸ click to answer",
  },
  marquee: { label: "LIVE from the hotline" },
  how: {
    label: "The whole bit",
    heading: "HOW IT WORKS",
    steps: [
      {
        n: "01",
        title: "Find the cart",
        body: "Look for the wooden cart rolling around the festival, draped in phone cords and disco light. Eight landline phones, all real, all ringing.",
        accent: "text-coral",
      },
      {
        n: "02",
        title: "Pick up",
        body: "Grab the receiver. A stranger's compliment plays — something someone said out loud because they meant it.",
        accent: "text-citrus",
      },
      {
        n: "03",
        title: "Record yours",
        body: "After the beep, leave a compliment for whoever picks up next. 30 seconds. Be kind. Be specific.",
        accent: "text-mint",
      },
      {
        n: "04",
        title: "Or leave one here",
        body: "Can't make it to the cart? Drop a text or audio compliment below. We'll pipe it into the rotation.",
        accent: "text-magenta",
      },
    ],
  },
  counter: { label: "Total compliments received" },
  submit: {
    label: "Your turn",
    heading: "LEAVE ONE",
    blurb: "Kind. Specific. 30 seconds or 500 characters. Gets queued, reviewed, then plays on a real phone at the next festival.",
  },
  footer: {
    wordmark: "COMPLIMENT HOTLINE",
    tagline: "An art piece, a cart, eight phones, and you.",
    copyright: "Compliment Hotline. All kindness reserved.",
  },
};

const cow: SiteCopy = {
  docTitle: "Cow Hotline — Pick up. Hear a cow-mpliment.",
  hero: {
    eyebrow: "🐄 An analog art piece for a digital disco — abducted for Burning Man 2026",
    wordTop: "COW-MPLIMENT",
    wordBottom: "HOTLINE",
    tagline:
      "There's no number to call. Leave a cow-mpliment here right now — then find the Cow Hotline at Illumination Village, Mootopia camp (4:30 & Esplanade) and hear the herd's voices play on real phones on playa.",
    ctaAria: "Pick up the phone to hear a cow-mpliment",
    ctaLine1: "pick up the phone",
    ctaLine2: "for a pick-me-moo",
    ctaHint: "▸ click to answer the herd",
  },
  marquee: { label: "LIVE from the herd" },
  how: {
    label: "The whole bit",
    heading: "HOW IT WORKS",
    steps: [
      {
        n: "01",
        title: "Find the herd",
        body: "No phone number. No app. The Cow Hotline is a wooden cart with eight landline phones, grazing at Illumination Village, Mootopia camp — 4:30 & Esplanade, Burning Man 2026. Follow the disco light and the moo.",
        accent: "text-coral",
      },
      {
        n: "02",
        title: "Pick up",
        body: "Grab the receiver. A stranger's cow-mpliment plays — something someone said out loud because they meant it.",
        accent: "text-citrus",
      },
      {
        n: "03",
        title: "Record yours",
        body: "After the beep, leave a cow-mpliment for whoever picks up next. 30 seconds. Be kind. Be specific. Moo optional.",
        accent: "text-mint",
      },
      {
        n: "04",
        title: "Load one now",
        body: "The lines are open. Load a text or audio cow-mpliment into the system below right now — it'll be waiting in the rotation when the cart lands at 4:30 & Esplanade, ready to play for whoever picks up.",
        accent: "text-magenta",
      },
    ],
  },
  counter: { label: "Total cow-mpliments received" },
  submit: {
    label: "Your turn, human",
    heading: "LEAVE ONE",
    blurb:
      "Kind. Specific. 30 seconds or 500 characters. Leave it now — it gets queued, reviewed, then plays on a real phone at our camp: 4:30 & Esplanade, Burning Man 2026.",
  },
  footer: {
    wordmark: "COW HOTLINE",
    tagline: "An art piece, a cart, eight phones, and moo.",
    note: "Illumination Village · Mootopia · 4:30 & Esplanade · Burning Man 2026",
    copyright: "Cow Hotline. All kindness re-herd.",
  },
};

export const copy: SiteCopy = COW_MODE ? cow : classic;
